import React from "react";
import { motion } from "framer-motion";
import Button from "../components/Button";
import { ExternalLink, MonitorPlay, Keyboard, Sparkles, Maximize2 } from "lucide-react";

const DEFAULT_DEMO_URL = "https://inplass-demo.preview.emergentagent.com/";
// When demo.inplass.com goes live, set REACT_APP_DEMO_URL=https://demo.inplass.com in frontend/.env
// (or edit the DEFAULT_DEMO_URL above). No code changes needed.
const DEMO_URL = process.env.REACT_APP_DEMO_URL || DEFAULT_DEMO_URL;

export default function Demo() {
  const [expanded, setExpanded] = React.useState(false);
  const features = [
    { icon: MonitorPlay, t: "Full HOP tour", d: "Live dashboard, analytics, SLA tracking." },
    { icon: Keyboard, t: "Hands-on flows", d: "Try guest requests, staff routing, reports." },
    { icon: Sparkles, t: "No signup", d: "Instant, in-browser, mobile ready." },
  ];

  return (
    <motion.main
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.4 }}
      data-testid="page-demo"
      className="relative"
    >
      {/* Background */}
      <div className="absolute inset-0 -z-10 pointer-events-none">
        <div className="absolute inset-0 bg-gradient-to-br from-brand-purple via-brand-purpleMid to-brand-purpleDeep" />
        <div className="absolute -top-40 -left-40 w-[600px] h-[600px] rounded-full bg-brand-purpleLight/30 blur-3xl" />
        <div className="absolute -bottom-40 -right-40 w-[600px] h-[600px] rounded-full bg-brand-orange/25 blur-3xl" />
        <div className="noise-overlay opacity-60" />
      </div>

      <section className="relative pt-[128px] md:pt-[160px] pb-10">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="max-w-3xl">
            <div className="font-display text-[11px] uppercase tracking-[0.28em] text-white/90 font-bold">
              Product demo
            </div>
            <h1
              className="mt-5 font-display text-white text-[46px] md:text-[74px] leading-[0.98] tracking-tight text-balance"
              style={{ fontWeight: 800 }}
            >
              See iNPLASS —{" "}
              <span className="grad-text">running your hotel.</span>
            </h1>
            <p className="mt-6 text-white/85 text-[17px] md:text-[19px] leading-relaxed max-w-2xl">
              Play with the live interactive product. No sign-up. Every screen
              is touchable — desktop and mobile.
            </p>
          </div>

          {/* Actions */}
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Button
              href={DEMO_URL}
              variant="primary"
              testId="launch-interactive-demo-btn"
              arrow={false}
              className="!px-6 !py-4 !text-[15px]"
            >
              <ExternalLink size={16} className="mr-1.5" />
              Launch full-screen demo
            </Button>
            <Button
              onClick={() => setExpanded((v) => !v)}
              variant="secondary"
              testId="expand-demo-btn"
              arrow={false}
            >
              <Maximize2 size={16} className="mr-1.5" />
              {expanded ? "Compact view" : "Expand view"}
            </Button>
            <Button to="/contact" variant="ghost" testId="demo-book-call-btn">
              Or book a live walkthrough
            </Button>
          </div>

          {/* Embedded demo */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9 }}
            className="relative mt-10 rounded-3xl overflow-hidden border border-white/20 bg-black shadow-[0_60px_120px_-30px_rgba(0,0,0,0.6)]"
            data-testid="demo-embed-wrapper"
          >
            {/* Browser chrome — clean, no URL exposed */}
            <div className="h-9 bg-white/5 border-b border-white/10 px-4 flex items-center gap-2">
              <div className="flex gap-1.5">
                <span className="w-2.5 h-2.5 rounded-full bg-red-400/70" />
                <span className="w-2.5 h-2.5 rounded-full bg-yellow-400/70" />
                <span className="w-2.5 h-2.5 rounded-full bg-emerald-400/70" />
              </div>
              <div className="ml-4 flex items-center gap-2 text-white/70 text-[11px] font-display font-bold uppercase tracking-[0.24em]">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulseGlow" />
                Live · iNPLASS interactive demo
              </div>
              <a
                href={DEMO_URL}
                target="_blank"
                rel="noreferrer"
                className="ml-auto flex items-center gap-1.5 text-white/70 hover:text-white text-[11px] font-semibold uppercase tracking-widest"
                data-testid="demo-open-new-tab"
              >
                Open <ExternalLink size={11} />
              </a>
            </div>
            <iframe
              title="iNPLASS interactive demo"
              src={DEMO_URL}
              className={`w-full ${expanded ? "h-[80vh]" : "h-[640px]"} bg-white`}
              data-testid="demo-iframe"
              loading="lazy"
              allow="clipboard-read; clipboard-write; fullscreen"
              referrerPolicy="strict-origin-when-cross-origin"
            />
          </motion.div>

          {/* Feature tiles */}
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3">
            {features.map((f) => (
              <div
                key={f.t}
                className="rounded-xl border border-white/15 bg-white/[0.06] backdrop-blur p-5"
                data-testid={`demo-feature-${f.t.toLowerCase().replace(/\s/g, "-")}`}
              >
                <f.icon size={18} className="text-white" />
                <div className="mt-3 text-white text-[15px] font-semibold">{f.t}</div>
                <div className="text-white/70 text-[13px]">{f.d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </motion.main>
  );
}
